import type { ComputedRef } from 'vue';
import { fetchClickIdData } from './clickId/api';
import {
  getClickIdCookie,
  getPixelCookieParams,
  getSessionClickID,
  persistClickIdCookie,
  persistSessionClickID,
} from './clickId/storage';
import { syncGatewayStoreTrackingCookies } from './clickId/helpers';
import { trackView } from './clickId/tracking';
import { buildInitialUrl, getTrackingParams } from './clickId/url';
import { getAttributionConfig } from './clickId/config';
import type { ClickIdFlowContext } from './clickId/types';

export function useClickID() {
  const { $logger } = useNuxtApp();
  const store = useKegelAnalyticStore();

  const getClickID = async (
    experimentId: ComputedRef<string | undefined>,
    variation: ComputedRef<string | undefined>,
  ): Promise<string | undefined> => {
    try {
      const context = buildContext();
      const clickId = await resolveClickId(context, experimentId, variation);

      if (clickId) {
        await applyClickId(clickId, context);
      }

      return clickId || undefined;
    } catch (error) {
      $logger.note({
        category: 'clickID',
        message: 'fetching the click id failed',
        level: 'warning',
        data: { experimentId: experimentId.value, variation: variation.value },
        cause: error,
      });
      return undefined;
    }
  };

  const buildContext = (): ClickIdFlowContext => {
    const config = getAttributionConfig();
    const clickIdCookie = getClickIdCookie();
    const { rtkfbp, rtkfbc, rtkttclid } = getPixelCookieParams();
    const { urlParams, pixelParams } = getTrackingParams(rtkfbp, rtkfbc);

    syncGatewayStoreTrackingCookies(rtkfbp, rtkfbc, rtkttclid);

    const campaignID = store.cmpId || config.campaignIds.default;
    const firstClickAttribution = config.lastPaidClickAttribution && campaignID === config.campaignIds.default
      ? true
      : config.firstClickAttribution;

    return {
      campaignID,
      cookieDomain: config.cookieDomain,
      cookieDuration: config.cookieDuration,
      firstClickAttribution,
      referrer: config.referrer,
      clickIdCookie,
      urlParams,
      pixelParams,
    };
  };

  const resolveClickId = async (
    context: ClickIdFlowContext,
    experimentId: ComputedRef<string | undefined>,
    variation: ComputedRef<string | undefined>,
  ): Promise<string | null> => {
    const rtkcid = context.urlParams.get('rtkcid');
    if (rtkcid) return rtkcid;

    const session = getSessionClickID();
    if (session && session !== 'undefined') return session;

    const initialSrc = buildInitialUrl(context.campaignID, context.referrer, experimentId, variation);
    const data = await fetchClickIdData(initialSrc + context.pixelParams, $logger);
    return data.clickid ?? null;
  };

  const applyClickId = async (clickId: string, context: ClickIdFlowContext): Promise<void> => {
    store.clickId = clickId;
    store.clickTime = Math.floor(Date.now() / 1000);

    persistSessionClickID(clickId);
    persistClickIdCookie(clickId, context.clickIdCookie, context.firstClickAttribution, context.cookieDomain, context.cookieDuration);

    try {
      await trackView(clickId, context.referrer);
    } catch (error) {
      $logger.note({
        category: 'clickID',
        message: 'view tracking failed',
        level: 'warning',
        cause: error,
      });
    }
  };

  return { getClickID };
}
